import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { KindText } from "@/components/KindK";
import { Globe, Palette, Menu, X, LogOut, Sparkles, Image as ImageIcon, Coins, Eye, Building2, HeartPulse, ShieldCheck, GitBranch, Flower2, MapPin, Loader2, Search as SearchIcon, Users, GraduationCap, Briefcase, BookOpen, Store, Leaf, Share2, FileText, Utensils, Film, CloudSun, Rss, Code2, Megaphone, Lightbulb, BarChart3, Zap, UserRound, LogIn, Type, ShoppingBag, Rocket, Video, FlaskConical, Wallet, Car, Bitcoin, Gift, Plus, Newspaper, UserPlus, Heart, HardDrive } from "lucide-react";
import { useApp, flagFromCode } from "@/context/AppContext";
import { BTC_ENABLED } from "@/lib/flags";
import { BrandingModal } from "@/components/BrandingModal";
import { FairPriceBadge } from "@/components/FairPriceBadge";
import { THEMES } from "@/themes";
import { api } from "@/lib/api";
import { MessageCircle, Orbit, ListChecks } from "lucide-react";
import { toast } from "sonner";

function HeaderPeopleSearch() {
  const nav = useNavigate();
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const timer = React.useRef(null);
  const boxRef = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const onChange = (e) => {
    const v = e.target.value; setQ(v); setOpen(true);
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/members/search?q=${encodeURIComponent(v.trim())}`); setResults(data.members || []); }
      catch { /* */ } finally { setLoading(false); }
    }, 300);
  };
  const go = (id) => { setOpen(false); setQ(""); setResults([]); nav(`/u/${id}`); };
  return (
    <div ref={boxRef} className="relative hidden md:block w-full max-w-xs" data-testid="header-search">
      <SearchIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
      <input data-testid="header-search-input" value={q} onFocus={() => setOpen(true)} onChange={onChange}
        placeholder="Search people…" className="rud-input w-full rounded-full pl-9 pr-3 py-2 text-sm" />
      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 mt-2 rud-card rounded-xl p-2 max-h-80 overflow-y-auto z-50 shadow-xl" data-testid="header-search-results">
          {loading ? <div className="py-4 text-center text-sec"><Loader2 size={16} className="animate-spin mx-auto" /></div>
            : results.length === 0 ? <p className="py-3 text-center text-sec text-xs">No members found.</p>
            : results.map((m) => (
              <button key={m.user_id} data-testid={`header-search-${m.user_id}`} onClick={() => go(m.user_id)}
                className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-white/10 text-left transition-colors">
                <span className="w-8 h-8 rounded-full bg-brand/15 text-brand grid place-items-center text-xs font-bold overflow-hidden shrink-0">
                  {m.avatar_url ? <img src={m.avatar_url} alt="" className="w-full h-full object-cover" /> : (m.name?.[0] || "?").toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm truncate">{m.name}</span>
                  <span className="block text-[11px] text-sec truncate">{m.city || m.headline || "Member"}</span>
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { t, user, logout, lang, setLang, meta, theme, setTheme, bgImage, setBgImage, country, setCountry, countryObj, ensureCountry, font, setFont, fonts, currency, setCurrency, cryptos } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const [bgInput, setBgInput] = useState(bgImage);
  const [countrySearch, setCountrySearch] = useState("");
  const [locating, setLocating] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [showMoreFonts, setShowMoreFonts] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    if (!user) { setChatUnread(0); return; }
    let live = true;
    const poll = () => api.get("/chat/unread").then(({ data }) => { if (live) setChatUnread(data.unread || 0); }).catch(() => {});
    poll();
    const id = setInterval(poll, 8000);
    return () => { live = false; clearInterval(id); };
  }, [user]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location is not supported on this device."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
          const d = await r.json();
          const code = d?.countryCode;
          if (code) {
            if (!(meta?.countries || []).some((c) => c.code === code)) {
              ensureCountry({ code, name: d.countryName || code, flag: flagFromCode(code) });
            }
            setCountry(code);
            toast.success(`Location set to ${d.countryName || code}`);
          } else {
            toast.error("Couldn't detect your location. Please pick your country manually.");
          }
        } catch { toast.error("Couldn't detect your location."); }
        finally { setLocating(false); }
      },
      () => { setLocating(false); toast.error("Location permission denied."); },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const links = [
    { to: "/personal", label: t("nav_personal"), icon: HeartPulse, primary: true, desc: "Your personal assistant: health regimen, interpret documents, summarize books, build your life story.",
      subnav: [
        { to: "/personal?tab=social", label: "Kind Social", icon: Users },
        { to: "/personal?tab=books", label: "Book Interpretation", icon: BookOpen },
        { to: "/personal?tab=carbon", label: "Carbon Footprint", icon: Leaf },
        { to: "/personal?tab=community", label: "Community", icon: Share2 },
        { to: "/personal?tab=insights", label: "Spend Analysis", icon: FileText },
        { to: "/personal?tab=regimen", label: "Health Regimen", icon: HeartPulse },
        { to: "/personal?tab=habits", label: "My Habits", icon: ListChecks },
        { to: "/personal?tab=receipts", label: "Product Safety", icon: FlaskConical },
        { to: "/learn", label: "VishwaGuru (Learn & Earn)", icon: GraduationCap },
        { to: "/personal?tab=story", label: "Life Story", icon: Film },
        { to: "/personal?tab=nearby", label: "Nearby", icon: MapPin },
        { to: "/personal?tab=weather", label: "Weather", icon: CloudSun },
      ] },
    { to: "/vault", label: t("nav_vault"), icon: ShieldCheck, desc: "Own your data & IP, earn from anonymized data, and host your apps." },
    { to: "/economy", label: t("nav_economy"), icon: Coins, desc: BTC_ENABLED ? "From your Referral, Activity and Data — cash out as money, Bitcoin or credits." : "From your Referral, Activity and Data — cash out as money or credits." },
    { to: "/prosperity", label: "Prosperity-Mint Money", icon: Zap, desc: "Your earning dashboard — credits, 3-tier referrals and prompt/blueprint royalties." },
    { to: "/transport", label: "Global Community Travel", icon: Car, desc: "Community carpool — share rides with fellow travellers, settle in credits, kinder & cheaper than a taxi." },
    { to: "/learn", label: "VishwaGuru-University", icon: GraduationCap, desc: "Learn & earn — courses, certificates and a growing learn-to-earn ecosystem for the whole community." },
    { to: "/meet", label: "Secure Meet", icon: Video, desc: "Launch an end-to-end secure My Kind video room and invite anyone with a link." },
  ];
  const languages = meta?.languages || [];
  // Location-aware selector. In Bharat (India): Hindi first, then English, then the Indian regional
  // languages (incl. Sanskrit) — foreign languages are hidden. Elsewhere: the global language set.
  const visibleLanguages = (() => {
    if (country === "IN") {
      const hi = languages.find((l) => l.code === "hi");
      const en = languages.find((l) => l.code === "en");
      const regional = languages.filter((l) => l.region === "IN");
      return [hi, en, ...regional].filter(Boolean);
    }
    return languages.filter((l) => !l.region);
  })();
  const countries = meta?.countries || [];
  const filteredCountries = countries.filter((c) => c.name.toLowerCase().includes(countrySearch.toLowerCase()));
  const cryptoList = Object.keys(cryptos || {});
  const localCur = countryObj?.currency || "USD";
  const curOptions = (() => {
    const out = [{ code: localCur, label: `${localCur} · Local`, group: "fiat" }];
    cryptoList.forEach((tk) => out.push({ code: tk, label: `${cryptos[tk].symbol ? cryptos[tk].symbol + " " : ""}${tk}`, group: "crypto" }));
    return out;
  })();
  const currencySymbol = cryptos?.[currency] ? (cryptos[currency].symbol || currency) : (meta?.currencies?.[currency]?.symbol || currency);

  const NavLinks = ({ onClick }) =>
    links.map((l) => (
      <div key={l.to} className="relative group">
        <Link
          to={l.to}
          onClick={onClick}
          data-testid={`nav-${l.to.slice(1)}`}
          className={
            l.primary
              ? `flex items-center gap-1.5 text-sm whitespace-nowrap rounded-full px-3.5 py-1.5 border font-medium transition-colors ${loc.pathname === l.to ? "bg-brand text-black border-brand" : "border-brand/40 text-[var(--rud-text)] hover:bg-brand/10 hover:border-brand"}`
              : `flex items-center gap-1.5 text-sm whitespace-nowrap transition-colors hover:text-brand ${loc.pathname === l.to ? "text-brand" : "text-sec"}`
          }
        >
          <l.icon size={15} /> {l.label}
        </Link>
        <div className="hidden min-[2000px]:block absolute left-1/2 -translate-x-1/2 top-full mt-3 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
          <div className="rounded-xl p-3 border border-rud text-xs text-sec leading-relaxed shadow-2xl bg-[var(--rud-surface)]" style={{ backdropFilter: "none" }}>
            <div className="flex items-center gap-2 text-[var(--rud-text)] font-medium mb-1"><l.icon size={14} className="text-brand" /> {l.label}</div>
            {l.subnav ? (
              <div className="flex flex-col mt-2">
                {l.subnav.map((s, i) => (
                  <Link key={i} to={s.to} onClick={onClick} data-testid={`subnav-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="px-2 py-1.5 rounded-md text-[var(--rud-text)] hover:bg-brand/10 hover:text-brand transition-colors flex items-center gap-2">
                    {s.icon && <s.icon size={14} className="text-brand shrink-0" />} {s.label}
                  </Link>
                ))}
              </div>
            ) : l.desc}
          </div>
        </div>
      </div>
    ));

  return (
    <header className="glass fixed top-0 inset-x-0 z-50" data-testid="app-header">
      <div className="w-full mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" onClick={() => { if (loc.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" }); else setTimeout(() => window.scrollTo({ top: 0 }), 0); }} className="flex items-center gap-2 shrink-0" data-testid="brand-logo" data-no-translate>
          <span className="font-serif text-2xl tracking-tight" data-testid="brand-name"><KindText text="My Kind" /></span>
        </Link>

        {/* Top-left cluster: search / ask ANXYA / earn — for signed-in members */}
        {user ? (
          <>
            <div className="flex items-center gap-2 min-w-0 ml-3">
              <HeaderPeopleSearch />
              <button data-testid="header-ask" onClick={() => window.dispatchEvent(new CustomEvent("open-anxya"))}
                className="hidden md:flex items-center gap-1.5 rounded-full h-9 px-3 text-sm text-sec border border-rud hover:text-brand hover:border-brand/50 transition-colors shrink-0">
                <Sparkles size={15} className="text-brand" /> Ask
              </button>
              <Link to="/economy" data-testid="header-earn"
                className="hidden md:flex items-center gap-1.5 rounded-full h-9 px-3 text-sm border border-brand/40 text-brand hover:bg-brand/10 transition-colors shrink-0">
                <Coins size={15} /> Earn
              </Link>
            </div>
            <div className="flex-1" />
          </>
        ) : <div className="flex-1" />}

        <nav className="hidden min-[2000px]:flex items-center gap-3">
          <NavLinks />
        </nav>

        <div className="flex items-center gap-2">
          {/* Fair-pricing by region */}
          <span className="hidden lg:inline-flex"><FairPriceBadge compact /></span>
          {/* Search (mobile icon) */}
          <Link to="/search" aria-label={t("nav_search")} data-testid="search-nav" className="btn-ghost rounded-full h-9 w-9 grid place-items-center sm:hidden">
            <SearchIcon size={16} />
          </Link>
          {/* Country */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label="Country" data-testid="country-selector" data-no-translate className="btn-ghost rounded-full h-9 px-3 hidden sm:flex items-center gap-2 text-sm">
                <span className="text-base leading-none">{countryObj?.flag || "🌐"}</span>
                <span className="hidden md:inline max-w-[130px] truncate">{countryObj?.name || "United States"}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass border-rud text-[var(--rud-text)] w-64">
              <DropdownMenuLabel>Country</DropdownMenuLabel>
              <div className="p-2 pb-1">
                <button data-testid="use-my-location" onClick={useMyLocation} disabled={locating}
                  className="btn-ghost rounded-md w-full h-9 px-2 flex items-center gap-2 text-xs disabled:opacity-60">
                  {locating ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} className="text-brand" />} Use my location
                </button>
              </div>
              <div className="p-2 pt-1">
                <input
                  data-testid="country-search"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder="Search country…"
                  className="rud-input w-full rounded-md px-2 py-1.5 text-xs"
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                {filteredCountries.map((c) => (
                  <DropdownMenuItem key={c.code} data-testid={`country-${c.code}`} onClick={() => setCountry(c.code)} className={`cursor-pointer focus:bg-white/10 ${country === c.code ? "text-brand" : ""}`}>
                    <span className="mr-2 text-base leading-none">{c.flag}</span> {c.name}
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Currency */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label="Currency" data-testid="currency-selector" data-no-translate className="btn-ghost rounded-full h-9 px-3 hidden sm:flex items-center gap-1.5 text-sm">
                {cryptos?.[currency] ? (currency === "BTC" ? <Bitcoin size={15} className="text-brand" /> : (cryptos[currency].symbol ? <span className="text-brand font-semibold text-sm leading-none">{cryptos[currency].symbol}</span> : null)) : <span className="text-brand font-semibold text-base leading-none">{currencySymbol}</span>}
                <span className="text-xs text-sec">{currency}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass border-rud text-[var(--rud-text)] w-52">
              <DropdownMenuLabel>Display currency</DropdownMenuLabel>
              <p className="px-2 pb-1 text-[10px] uppercase tracking-widest text-sec">Local</p>
              {curOptions.filter((c) => c.group === "fiat").map((c) => (
                <DropdownMenuItem key={c.code} data-testid={`currency-${c.code}`} onClick={() => setCurrency(c.code)} className={`cursor-pointer focus:bg-white/10 ${currency === c.code ? "text-brand" : ""}`}>
                  {c.label}
                </DropdownMenuItem>
              ))}
              {BTC_ENABLED && (
                <>
                  <p className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-sec">Crypto</p>
                  {curOptions.filter((c) => c.group === "crypto").map((c) => (
                    <DropdownMenuItem key={c.code} data-testid={`currency-${c.code}`} onClick={() => setCurrency(c.code)} className={`cursor-pointer focus:bg-white/10 ${currency === c.code ? "text-brand" : ""}`}>
                      {c.label}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <p className="px-2 py-1.5 text-[10px] text-sec leading-snug">Prices show in your local currency by default · switch anytime.</p>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label={t("language")} data-testid="language-selector" className="btn-ghost rounded-full h-9 px-3 flex items-center gap-2 text-sm">
                <Globe size={16} /> <span className="hidden sm:inline">{languages.find((x) => x.code === lang)?.native || "EN"}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass border-rud text-[var(--rud-text)] max-h-80 overflow-y-auto">
              <DropdownMenuLabel>{t("language")}</DropdownMenuLabel>
              {country === "IN" && <p className="px-2 pb-1 text-[10px] uppercase tracking-widest text-sec">English default · Bharat languages + Sanskrit</p>}
              {visibleLanguages.map((l) => (
                <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)} className="cursor-pointer focus:bg-white/10">
                  {l.native} <span className="ml-2 text-sec text-xs">{l.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label={t("theme")} data-testid="theme-selector" className="btn-ghost rounded-full h-9 w-9 hidden sm:grid place-items-center">
                <Palette size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass border-rud text-[var(--rud-text)] w-64">
              <DropdownMenuLabel>{t("theme")}</DropdownMenuLabel>
              <p className="px-2 pb-1 text-[10px] text-sec leading-snug">🌱 Black BG themes use 1% fewer credits — dark screens save energy.</p>
              <div className="grid grid-cols-2 gap-1 p-1">
                {Object.entries(THEMES).map(([key, val]) => (
                  <button
                    key={key}
                    data-testid={`theme-${key}`}
                    onClick={() => setTheme(key)}
                    className={`flex items-center gap-2 rounded-md px-2 py-2 text-xs hover:bg-white/10 ${theme === key ? "bg-white/10" : ""}`}
                  >
                    <span className="w-4 h-4 rounded-full border border-white/20" style={{ background: val.swatch }} />
                    {val.label}
                  </button>
                ))}
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuLabel className="flex items-center gap-2"><ImageIcon size={14} /> Custom background</DropdownMenuLabel>
              <div className="p-2 space-y-2">
                <input
                  data-testid="bg-image-input"
                  value={bgInput}
                  onChange={(e) => setBgInput(e.target.value)}
                  placeholder="Paste image URL…"
                  className="rud-input w-full rounded-md px-2 py-1.5 text-xs"
                />
                <div className="flex gap-2">
                  <button data-testid="bg-apply" onClick={() => setBgImage(bgInput)} className="btn-primary text-xs px-3 py-1.5 flex-1">Apply</button>
                  <button data-testid="bg-clear" onClick={() => { setBgInput(""); setBgImage(""); }} className="btn-ghost rounded-md text-xs px-3 py-1.5">Clear</button>
                </div>
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuLabel className="flex items-center gap-2"><Type size={14} /> Font</DropdownMenuLabel>
              <div className="grid grid-cols-3 gap-1 p-1">
                {Object.entries(fonts).filter(([, v]) => v.primary || showMoreFonts).map(([key, val]) => (
                  <button key={key} data-testid={`font-${key}`} onClick={() => setFont(key)}
                    style={{ fontFamily: val.stack }}
                    className={`rounded-md px-2 py-2 text-xs hover:bg-white/10 ${font === key ? "bg-white/10 text-brand" : ""}`}>
                    {val.label}
                  </button>
                ))}
              </div>
              <button data-testid="font-more" onClick={() => setShowMoreFonts((v) => !v)} className="text-[11px] text-sec hover:text-brand px-3 pb-2">
                {showMoreFonts ? "Fewer options" : "More options"}
              </button>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Visible credit meter + buy action (desktop; mobile uses the account menu) */}
          {user && (
            <div className="hidden sm:flex items-center gap-1.5 mr-0.5" data-testid="credit-meter">
              <span data-testid="header-credits" className="flex items-center gap-1 text-brand text-sm rounded-full border border-brand/30 bg-brand/10 px-2.5 h-8">
                <Coins size={14} /> {user.credits >= 1000 ? new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(user.credits) : Number(user.credits || 0).toFixed(2)}
              </span>
              <button data-testid="buy-credits-btn"
                onClick={() => window.dispatchEvent(new CustomEvent("open-recharge", { detail: { message: "Buy more credits" } }))}
                className="btn-primary rounded-full h-8 px-2.5 sm:px-3 text-xs font-medium flex items-center gap-1 whitespace-nowrap">
                <Plus size={13} /> <span className="hidden sm:inline">Buy credits</span><span className="sm:hidden">Buy</span>
              </button>
            </div>
          )}

          {/* Kind Chat — quick access with unread badge */}
          {user && (
            <Link to="/chat" data-testid="header-chat-btn" aria-label="Kind Chat"
              className="btn-ghost rounded-full h-9 w-9 grid place-items-center relative">
              <MessageCircle size={18} />
              {chatUnread > 0 && (
                <span data-testid="header-chat-unread" className="absolute -top-1 -right-1 bg-brand text-black text-[10px] rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none">
                  {chatUnread > 99 ? "99+" : chatUnread}
                </span>
              )}
            </Link>
          )}

          {/* Unified profile menu — avoids header overflow */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="profile-menu" aria-label="Profile menu"
                className="btn-ghost rounded-full h-9 pl-1.5 pr-2 flex items-center gap-1.5 text-sm">
                {user ? (
                  <>
                    <span data-testid="header-credits-mobile" className="sm:hidden flex items-center gap-0.5 text-brand text-xs">
                      <Coins size={12} /> {user.credits >= 1000 ? new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(user.credits) : Number(user.credits || 0).toFixed(2)}
                    </span>
                    <span className="w-6 h-6 rounded-full bg-brand text-black grid place-items-center text-[11px] font-bold shrink-0 overflow-hidden">
                      {(user.social_profile?.photo || user.picture)
                        ? <img src={user.social_profile?.photo || user.picture} alt="" className="w-full h-full object-cover" />
                        : (user.name || user.email || "U").slice(0, 1).toUpperCase()}
                    </span>
                  </>
                ) : (
                  <span className="w-6 h-6 grid place-items-center"><UserRound size={18} /></span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass border-rud text-[var(--rud-text)] w-60 max-h-[85vh] overflow-y-auto">
              {user ? (
                <>
                  <DropdownMenuLabel className="truncate">{user.name || user.email}</DropdownMenuLabel>
                  <div className="px-2 pb-2 text-xs text-sec flex items-center gap-1.5"><Coins size={13} className="text-brand" /> {user.credits} credits</div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-sec">You</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => nav("/personal")} className="cursor-pointer focus:bg-white/10 rud-hl"><HeartPulse size={14} className="mr-2" /> Profile</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-chat" onClick={() => nav("/chat")} className="cursor-pointer focus:bg-white/10 rud-hl"><MessageCircle size={14} className="mr-2" /> Kind Chat{chatUnread > 0 && <span className="ml-auto bg-brand text-black text-[10px] rounded-full px-1.5">{chatUnread}</span>}</DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-sec">Money & assets</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="menu-vault" onClick={() => nav("/vault")} className="cursor-pointer focus:bg-white/10 rud-hl"><ShieldCheck size={14} className="mr-2" /> My Vault</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-smart-drive" onClick={() => nav("/drive")} className="cursor-pointer focus:bg-white/10 rud-hl"><HardDrive size={14} className="mr-2" /> Smart Drive</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-buy-credits" onClick={() => window.dispatchEvent(new CustomEvent("open-recharge", { detail: { message: "Buy more credits" } }))} className="cursor-pointer focus:bg-white/10 rud-hl"><Plus size={14} className="mr-2" /> Buy more credits</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-earnings" onClick={() => nav("/economy")} className="cursor-pointer focus:bg-white/10 rud-hl"><Coins size={14} className="mr-2" /> My Earnings</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-invite-earn" onClick={() => nav("/economy")} className="cursor-pointer focus:bg-white/10 rud-hl"><Gift size={14} className="mr-2" /> Invite &amp; Earn</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-invite-connections" onClick={() => nav("/invite")} className="cursor-pointer focus:bg-white/10 rud-hl"><UserPlus size={14} className="mr-2" /> Invite connections</DropdownMenuItem>
                  {user?.role === "admin" && <DropdownMenuItem data-testid="menu-manage-invites" onClick={() => nav("/admin/access")} className="cursor-pointer focus:bg-white/10 rud-hl"><ShieldCheck size={14} className="mr-2" /> Manage invites</DropdownMenuItem>}
                  <DropdownMenuItem data-testid="menu-prosperity" onClick={() => nav("/prosperity")} className="cursor-pointer focus:bg-white/10 rud-hl"><Zap size={14} className="mr-2" /> Prosperity-Mint Money</DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-sec">Global Community</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="menu-community-travel" onClick={() => nav("/transport")} className="cursor-pointer focus:bg-white/10 rud-hl"><Car size={14} className="mr-2" /> Global Community Travel</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-community-events" onClick={() => nav("/events")} className="cursor-pointer focus:bg-white/10 rud-hl"><Utensils size={14} className="mr-2" /> Community Events</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-community-meals" onClick={() => nav("/meals")} className="cursor-pointer focus:bg-white/10 rud-hl"><Leaf size={14} className="mr-2" /> Community Meals</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-bookings" onClick={() => nav("/bookings")} className="cursor-pointer focus:bg-white/10 rud-hl"><Gift size={14} className="mr-2" /> My Bookings</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-news" onClick={() => nav("/news")} className="cursor-pointer focus:bg-white/10 rud-hl"><Newspaper size={14} className="mr-2" /> Good News</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-practice" onClick={() => nav("/practice")} className="cursor-pointer focus:bg-white/10 rud-hl"><Flower2 size={14} className="mr-2" /> Daily Practice</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-galactopedia" onClick={() => nav("/galactopedia")} className="cursor-pointer focus:bg-white/10 rud-hl"><Orbit size={14} className="mr-2" /> Galactopedia — Let's build together</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-community-university" onClick={() => nav("/learn")} className="cursor-pointer focus:bg-white/10 rud-hl"><GraduationCap size={14} className="mr-2" /> VishwaGuru-University</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-teachers" onClick={() => nav("/teachers")} className="cursor-pointer focus:bg-white/10 rud-hl"><Users size={14} className="mr-2" /> Trending Teachers</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-teacher-studio" onClick={() => nav("/teach")} className="cursor-pointer focus:bg-white/10 rud-hl"><GraduationCap size={14} className="mr-2" /> Teacher Studio</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-secure-meet" onClick={() => nav("/meet")} className="cursor-pointer focus:bg-white/10 rud-hl"><Video size={14} className="mr-2" /> Secure Meet</DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-sec">More</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="menu-affiliate" onClick={() => window.open("/affiliate", "_blank", "noopener")} className="cursor-pointer focus:bg-white/10 rud-hl"><Share2 size={14} className="mr-2" /> Affiliate Partner</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-anxya-story" onClick={() => nav("/about")} className="cursor-pointer focus:bg-white/10 rud-hl"><Flower2 size={14} className="mr-2" /> The My Kind Story</DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="open-branding" onClick={() => setBrandingOpen(true)} className="cursor-pointer focus:bg-white/10 rud-hl"><Building2 size={14} className="mr-2" /> Brand & settings</DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="cursor-pointer focus:bg-white/10 rud-hl text-red-400"><LogOut size={14} className="mr-2" /> {t("logout")}</DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-sec">Global Community</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="menu-community-travel" onClick={() => nav("/transport")} className="cursor-pointer focus:bg-white/10 rud-hl"><Car size={14} className="mr-2" /> Global Community Travel</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-community-events" onClick={() => nav("/events")} className="cursor-pointer focus:bg-white/10 rud-hl"><Utensils size={14} className="mr-2" /> Community Events</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-community-meals" onClick={() => nav("/meals")} className="cursor-pointer focus:bg-white/10 rud-hl"><Leaf size={14} className="mr-2" /> Community Meals</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-bookings" onClick={() => nav("/bookings")} className="cursor-pointer focus:bg-white/10 rud-hl"><Gift size={14} className="mr-2" /> My Bookings</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-news" onClick={() => nav("/news")} className="cursor-pointer focus:bg-white/10 rud-hl"><Newspaper size={14} className="mr-2" /> Good News</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-practice" onClick={() => nav("/practice")} className="cursor-pointer focus:bg-white/10 rud-hl"><Flower2 size={14} className="mr-2" /> Daily Practice</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-galactopedia" onClick={() => nav("/galactopedia")} className="cursor-pointer focus:bg-white/10 rud-hl"><Orbit size={14} className="mr-2" /> Galactopedia — Let's build together</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-community-university" onClick={() => nav("/learn")} className="cursor-pointer focus:bg-white/10 rud-hl"><GraduationCap size={14} className="mr-2" /> VishwaGuru-University</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-teachers" onClick={() => nav("/teachers")} className="cursor-pointer focus:bg-white/10 rud-hl"><Users size={14} className="mr-2" /> Trending Teachers</DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel>Welcome to My Kind</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="header-login" onClick={() => nav("/auth")} className="cursor-pointer focus:bg-white/10 rud-hl"><LogIn size={14} className="mr-2" /> Sign in</DropdownMenuItem>
                  <DropdownMenuItem data-testid="header-get-started" onClick={() => nav("/auth")} className="cursor-pointer focus:bg-white/10 rud-hl text-brand"><Sparkles size={14} className="mr-2" /> Get started</DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-sec">More</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="menu-anxya-story" onClick={() => nav("/about")} className="cursor-pointer focus:bg-white/10 rud-hl"><Flower2 size={14} className="mr-2" /> The My Kind Story</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-invite-earn" onClick={() => nav("/economy")} className="cursor-pointer focus:bg-white/10 rud-hl"><Gift size={14} className="mr-2" /> Invite &amp; Earn</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-affiliate" onClick={() => window.open("/affiliate", "_blank", "noopener")} className="cursor-pointer focus:bg-white/10 rud-hl"><Share2 size={14} className="mr-2" /> Affiliate Partner</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <button className="min-[2000px]:hidden btn-ghost rounded-full h-9 w-9 grid place-items-center" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="min-[2000px]:hidden overflow-hidden border-t border-rud">
            <div className="px-6 py-4 flex flex-col gap-4">
              <NavLinks onClick={() => setOpen(false)} />
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-rud">
                <label className="text-xs text-sec col-span-3">Preferences</label>
                <select data-testid="mobile-language" value={lang} onChange={(e) => setLang(e.target.value)} className="rud-input rounded-md px-2 py-2 text-xs col-span-3 sm:col-span-1">
                  {visibleLanguages.map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}
                </select>
                <select data-testid="mobile-theme" value={theme} onChange={(e) => setTheme(e.target.value)} className="rud-input rounded-md px-2 py-2 text-xs col-span-3 sm:col-span-1">
                  {Object.entries(THEMES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select data-testid="mobile-country" value={country} onChange={(e) => setCountry(e.target.value)} className="rud-input rounded-md px-2 py-2 text-xs col-span-3 sm:col-span-1">
                  {countries.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                </select>
                <select data-testid="mobile-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="rud-input rounded-md px-2 py-2 text-xs col-span-3 sm:col-span-1">
                  {curOptions.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
              {!user && <Link to="/auth" onClick={() => setOpen(false)} className="btn-primary h-9 px-4 grid place-items-center text-sm">{t("get_started")}</Link>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {brandingOpen && <BrandingModal onClose={() => setBrandingOpen(false)} />}
      </AnimatePresence>
    </header>
  );
}
